import React, { memo, useCallback, useMemo } from "react";
import {
  Button,
  Center,
  Divider,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  HStack,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import colors from "app/colors";
import { AccountBoxIcon, CirclePlusIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { useToggle } from "react-use";
import InternalAdminEditUserAttributeCategoryName from "../components/internal-admin-edit-user-attribute-category-name";
import useQueryString from "modules/hooks/use-query-string";
import InternalAdminEditUserAttribute from "../components/internal-admin-edit-user-attribute";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import useInternalAdminUserAttributeCategories from "../hooks/use-internal-admin-user-attribute-categories";
import useInternalAdminUserAttributeList from "../hooks/use-internal-admin-user-attribute-list";
import { sortByName } from "modules/utils/Functions";

interface IProps {
  userAttributeCategoryId: string;
}

const InternalAdminUserAttributeCategoryDrawer = ({
  userAttributeCategoryId,
}: IProps) => {
  const { openModal } = useOverlay();
  const { qsOrganizationId } = useQueryString();
  const [isDisplayInactive, toggleDisplayInactive] = useToggle(false);

  const { userAttributeCategory } = useInternalAdminUserAttributeCategories(
    userAttributeCategoryId,
  );

  const { userAttributes, refetch } = useInternalAdminUserAttributeList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
      userAttributeCategoryId,
    },
  });

  const activeUserAttributes = useMemo(
    () =>
      [...userAttributes]
        .filter((userAttribute) => userAttribute.active)
        .sort(sortByName),
    [userAttributes],
  );

  const inactiveUserAttributes = useMemo(
    () =>
      [...userAttributes]
        .filter((userAttribute) => !userAttribute.active)
        .sort(sortByName),
    [userAttributes],
  );

  const onAddAttributeClicked = useCallback(() => {
    openModal({
      type: MODAL_TYPE.INTERNAL_ADMIN_CREATE_USER_ATTRIBUTE,
      id: userAttributeCategoryId,
      onSaveCallback: refetch,
    });
  }, [openModal, refetch, userAttributeCategoryId]);

  if (!userAttributeCategory) {
    return null;
  }

  if (userAttributeCategory.tagCategoryId) {
    return (
      <DrawerContent>
        <DrawerCloseButton left={6} top={8} color={colors.gray30} size="lg" />
        <DrawerHeader pt={8}>
          <Center fontWeight={700} fontFamily="Outfit" fontSize={26}>
            User Attribute Category Details
          </Center>
        </DrawerHeader>
        <DrawerBody p={0}>
          <Stack spacing={6} px={8}>
            <InternalAdminEditUserAttributeCategoryName
              userAttributeCategory={userAttributeCategory}
            />
            <VStack spacing={3} w="full" alignItems="flex-start">
              <HStack
                w="full"
                borderBottomWidth={1}
                borderBottomColor={colors.black}
              >
                <AccountBoxIcon boxSize={6} />
                <Text fontWeight={600} fontSize={18}>
                  {`Attributes (${activeUserAttributes.length})`}
                </Text>
                <Spacer />
                <SyncoTooltip label="This is a userAttribute used as a user attribute">
                  <HStack opacity={0.5} spacing={1} cursor="pointer">
                    <CirclePlusIcon boxSize={4} fill={colors.orangePrimary} />
                    <Text
                      color={colors.orangePrimary}
                      fontSize={12}
                      fontWeight={500}
                    >
                      Add Attribute
                    </Text>
                  </HStack>
                </SyncoTooltip>
              </HStack>
              <VStack w="full" alignItems="flex-start" divider={<Divider />}>
                {activeUserAttributes.map((userAttribute) => (
                  <Text
                    key={userAttribute.id}
                    fontSize={14}
                    fontWeight={600}
                    color={colors.gray50}
                    w="full"
                  >
                    {userAttribute.name}
                  </Text>
                ))}
              </VStack>
            </VStack>
            {inactiveUserAttributes.length && (
              <VStack spacing={3} w="full" alignItems="flex-start">
                <Button
                  variant="link"
                  color={colors.blue}
                  fontWeight={500}
                  fontSize={12}
                  onClick={toggleDisplayInactive}
                >
                  {isDisplayInactive
                    ? `+ Show Deactivated Attributes (${inactiveUserAttributes.length})`
                    : `- Hide Deactivated Attributes (${inactiveUserAttributes.length})`}
                </Button>
                {!isDisplayInactive && (
                  <VStack
                    w="full"
                    alignItems="flex-start"
                    divider={<Divider />}
                  >
                    {inactiveUserAttributes.map((userAttribute) => (
                      <Text
                        key={userAttribute.id}
                        fontSize={14}
                        fontWeight={500}
                        color={colors.grayMain}
                        fontStyle="italic"
                        w="full"
                      >
                        {userAttribute.name}
                      </Text>
                    ))}
                  </VStack>
                )}
              </VStack>
            )}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    );
  }

  return (
    <DrawerContent>
      <DrawerCloseButton left={6} top={8} color={colors.gray30} size="lg" />
      <DrawerHeader pt={8}>
        <Center fontWeight={700} fontFamily="Outfit" fontSize={26}>
          User Attribute Category Details
        </Center>
      </DrawerHeader>
      <DrawerBody p={0}>
        <Stack spacing={6} px={8}>
          <InternalAdminEditUserAttributeCategoryName
            userAttributeCategory={userAttributeCategory}
          />
          <VStack spacing={3} w="full" alignItems="flex-start">
            <HStack
              w="full"
              borderBottomWidth={1}
              borderBottomColor={colors.black}
            >
              <AccountBoxIcon boxSize={6} />
              <Text fontWeight={600} fontSize={18}>
                {`Attributes (${activeUserAttributes.length})`}
              </Text>
              <Spacer />
              <Button
                variant="ghost"
                color={colors.orangePrimary}
                fontSize={12}
                fontWeight={500}
                size="xs"
                leftIcon={
                  <CirclePlusIcon boxSize={4} fill={colors.orangePrimary} />
                }
                onClick={onAddAttributeClicked}
              >
                Add Attribute
              </Button>
            </HStack>
            <VStack w="full" alignItems="flex-start" divider={<Divider />}>
              {activeUserAttributes.map((userAttribute) => (
                <InternalAdminEditUserAttribute
                  userAttribute={userAttribute}
                  key={userAttribute.id}
                />
              ))}
            </VStack>
          </VStack>
          {inactiveUserAttributes.length && (
            <VStack spacing={3} w="full" alignItems="flex-start">
              <Button
                variant="link"
                color={colors.blue}
                fontWeight={500}
                fontSize={12}
                onClick={toggleDisplayInactive}
              >
                {isDisplayInactive
                  ? `+ Show Deactivated Attributes (${inactiveUserAttributes.length})`
                  : `- Hide Deactivated Attributes (${inactiveUserAttributes.length})`}
              </Button>
              {!isDisplayInactive && (
                <VStack w="full" alignItems="flex-start" divider={<Divider />}>
                  {inactiveUserAttributes.map((userAttribute) => (
                    <InternalAdminEditUserAttribute
                      userAttribute={userAttribute}
                      key={userAttribute.id}
                    />
                  ))}
                </VStack>
              )}
            </VStack>
          )}
        </Stack>
      </DrawerBody>
    </DrawerContent>
  );
};

export default memo(InternalAdminUserAttributeCategoryDrawer);
